import * as React from 'react';
import type { OverflowItemProps } from '@fluentui/react-components';
import {
  Body1,
  Button,
  Image,
  Link,
  makeStyles,
  Menu,
  MenuButton,
  MenuItem,
  MenuItemLink,
  MenuList,
  MenuPopover,
  MenuTrigger,
  mergeClasses,
  Overflow,
  OverflowItem,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  shorthands,
  Subtitle1,
  tokens,
  useIsOverflowItemVisible,
  useOverflowMenu,
} from '@fluentui/react-components';
import { NavigationRegular } from '@fluentui/react-icons';
import { useMediaQuery } from '@react-hook/media-query';
import { defineMessage, useIntl } from 'react-intl';
import { useUserQuery } from '@foundershub/api-client';
import MicrosoftLogo from '../media/logos/MicrosoftFull.svg';

interface HeaderItem {
  key: string;
  label: string | React.ReactNode;
  href?: string;
  hide?: boolean;
  onClick?: () => void;
  target?: string;
}

export function LoggedOutHeader() {
  const intl = useIntl();
  const isSmall = useMediaQuery('screen and (max-width: 857px)');
  const classes = useStyles();
  const { isError: isUserLoggedOut } = useUserQuery();

  const loggedOutNavItems: Array<HeaderItem> = [
    {
      key: 'ms4su',
      label: isSmall ? ms4suText : <Subtitle1 className={classes.ms4suText}>{ms4suText}</Subtitle1>,
      href: 'https://www.microsoft.com/startups/',
    },
    {
      key: 'support',
      label: intl.formatMessage(supportLabel),
      href: 'https://learn.microsoft.com/en-us/microsoft-for-startups/',
    },
    {
      key: 'blog',
      label: intl.formatMessage(blogLabel),
      href: 'https://startups.microsoft.com/blog',
    },
    {
      key: 'stories',
      label: intl.formatMessage(storiesLabel),
      href: 'https://www.microsoft.com/startups/founder-stories',
    },
    {
      key: 'ai',
      label: intl.formatMessage(aiLabel),
      href: 'https://www.microsoft.com/startups/ai',
    },
    {
      key: 'pegasus',
      label: intl.formatMessage(pegasusLabel),
      href: 'https://www.microsoft.com/startups/pegasus',
    },
    {
      key: 'signin',
      label: intl.formatMessage(signInLabel),
      href: '/login',
      hide: !isUserLoggedOut,
      target: '_self',
    },
    {
      key: 'signout',
      label: intl.formatMessage(signOutLabel),
      href: '/logout',
      hide: isUserLoggedOut,
      target: '_self',
    },
  ];

  const OverflowMenuItem: React.FC<
    Pick<OverflowItemProps, 'id'> & {
      href?: string;
      label: string | React.ReactNode;
      onClick?: React.MouseEventHandler<HTMLDivElement>;
      target?: string;
    }
  > = (props) => {
    const { id, href, label, onClick, target } = props;
    const isVisible = useIsOverflowItemVisible(id);

    if (isVisible) {
      return null;
    }

    if (onClick) {
      return <MenuItem onClick={onClick}>{label}</MenuItem>;
    }

    return (
      <MenuItemLink href={href ?? ''} as="a" target={target ?? '_blank'}>
        {id === 'ms4su' ? ms4suText : label}
      </MenuItemLink>
    );
  };

  const OverflowMenu: React.FC = () => {
    const { ref, isOverflowing } = useOverflowMenu<HTMLButtonElement>();

    if (!isOverflowing) {
      return null;
    }

    return (
      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <MenuButton ref={ref} appearance="transparent" size="small">
            <Body1>{intl.formatMessage(moreLabel)}</Body1>
          </MenuButton>
        </MenuTrigger>

        <MenuPopover>
          <MenuList>
            {loggedOutNavItems.map(({ key, label, href, hide, onClick, target }) => {
              return hide ? null : (
                <OverflowMenuItem
                  key={key}
                  id={key}
                  href={href ?? ''}
                  target={target ?? '_blank'}
                  label={label}
                  onClick={onClick}
                />
              );
            })}
          </MenuList>
        </MenuPopover>
      </Menu>
    );
  };

  const nav = (
    <div role="list" className={mergeClasses(classes.links, isSmall && classes.linksDropdown)}>
      {loggedOutNavItems.map(({ key, label, href, hide, onClick, target }: HeaderItem) =>
        hide ? null : (
          <OverflowItem id={key} key={key}>
            <div role="listitem">
              <Link
                target={target ?? '_blank'}
                appearance="subtle"
                href={href ?? ''}
                onClick={onClick}
                className={classes.link}
              >
                {label}
              </Link>
            </div>
          </OverflowItem>
        )
      )}
      <div role="listitem">
        <OverflowMenu />
      </div>
    </div>
  );

  const msftIcon = (
    <Link href="https://www.microsoft.com/" className={classes.logo} target="_blank" rel="noopener noreferrer">
      <Image src={MicrosoftLogo} alt="Microsoft" width="123" height={isSmall ? 40 : 27} />
    </Link>
  );

  return (
    <header role="banner" className={classes.root}>
      {isSmall ? (
        <div className={classes.smallContainer}>
          <Popover trapFocus>
            <PopoverTrigger disableButtonEnhancement>
              <Button
                appearance="subtle"
                icon={<NavigationRegular />}
                title={intl.formatMessage(showNavigationLabel)}
                size="large"
              />
            </PopoverTrigger>
            <PopoverSurface>{nav}</PopoverSurface>
          </Popover>
          {msftIcon}
          <span className={classes.empty}></span>
        </div>
      ) : (
        <div className={classes.container}>
          <span className={classes.brand}>{msftIcon}</span>
          <Overflow>{nav}</Overflow>
        </div>
      )}
    </header>
  );
}

const ms4suText = 'Microsoft for Startups';

const supportLabel = defineMessage({
  defaultMessage: 'Support',
  description: 'global nav link to startups support page',
});

const blogLabel = defineMessage({
  defaultMessage: 'Blog',
  description: 'global nav link to startups blog',
});

const storiesLabel = defineMessage({
  defaultMessage: 'Founder stories',
  description: 'global nav link to startups stories',
});

const aiLabel = defineMessage({
  defaultMessage: 'Build with AI',
  description: 'global nav link to startups AI page',
});

const pegasusLabel = defineMessage({
  defaultMessage: 'Specialty programs',
  description: 'global nav link to startups Pegasus page',
});

const signInLabel = defineMessage({
  defaultMessage: 'Founders Hub sign in',
  description: 'global nav link to foundershub sign in page',
});

const signOutLabel = defineMessage({
  defaultMessage: 'Sign Out',
  description: 'global nav link to sign out',
});

const moreLabel = defineMessage({
  defaultMessage: 'More',
  description: 'global nav link to show more options',
});

const showNavigationLabel = defineMessage({
  defaultMessage: 'Show navigation',
  description: 'Button label to show navigation menu',
});

const useStyles = makeStyles({
  root: {
    backgroundColor: tokens.colorNeutralBackground1,
    display: 'inline-flex',
    alignItems: 'center',
    boxShadow: tokens.shadow4,
  },
  container: {
    display: 'flex',
    ...shorthands.margin('13px', 0, '15px', '182px'),
    flexWrap: 'nowrap',
    ...shorthands.overflow('hidden'),
    columnGap: tokens.spacingHorizontalXS,
    '@media screen and (max-width: 1439px)': {
      width: '1076px',
      ...shorthands.padding('0', '16px'),
      ...shorthands.margin('13px', 'auto', '15px', 'auto'),
    },
  },
  smallContainer: {
    display: 'flex',
    ...shorthands.margin('13px', 0, '15px', 0),
    width: '100%',
    justifyContent: 'space-between',
  },
  brand: {
    display: 'flex',
    columnGap: tokens.spacingHorizontalM,
    alignItems: 'center',
    ':hover': {
      cursor: 'pointer',
    },
  },
  link: {
    whiteSpace: 'nowrap',
    ...shorthands.overflow('hidden'),
    ...shorthands.margin(0, '8px'),
    '&:focus-visible': {
      outline: '1.5px solid', // Customize the box style as needed
      outlineOffset: '1px',
      textDecoration: 'none', // Remove default underline
      boxShadow: '0 0 0 1px',
    },
  },
  links: {
    display: 'flex',
    alignItems: 'baseline',
    flexDirection: 'row',
    width: '823px',
  },
  linksDropdown: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM,
    minWidth: '100px',
    width: '100%',
  },
  empty: {
    display: 'inline-block',
    width: '32px',
  },
  logo: {
    display: 'flex',
    paddingInlineEnd: tokens.spacingHorizontalM,
    borderInlineEndColor: tokens.colorNeutralForeground1,
    borderInlineEndWidth: tokens.strokeWidthThick,
    borderInlineEndStyle: 'solid',
    fontWeight: tokens.fontWeightSemibold,
    '@media screen and (max-width: 857px)': {
      borderInlineEndStyle: 'none',
    },
    ':focus > img': {
      outline: '1.5px solid black', // Customize the box style as needed
      outlineOffset: '1px',
      textDecoration: 'none', // Remove default underline
      boxShadow: '0 0 0 1px',
    },
  },
  ms4suText: {
    ...shorthands.overflow('hidden'),
    whiteSpace: 'nowrap',
  },
});
